/**
 * Safe, regex-error-proof keyword utilities.
 * Uses the escape + matcher helpers so terms like "C++", "A/B", "Node.js"
 * never trigger "Invalid regular expression: Nothing to repeat".
 */

import { ANALYSIS_CONFIG } from '@config/constants';
import { makeWordMatcher } from '@utils/regex';

/** Normalize text for lightweight keyword extraction */
function normalize(text = '') {
  return String(text)
    .replace(/\s+/g, ' ')
    .trim();
}

/** Split job text into candidate keywords (kept simple & fast) */
export function extractKeywordsFromText(jobText = '') {
  const stop = ANALYSIS_CONFIG?.stopWords || new Set();
  const words = normalize(jobText)
    // keep letters, digits, + . / & - (so "C++", "Node.js", "A/B" survive)
    .match(/[A-Za-z0-9+./&-]+/g) || [];

  // de-duplicate while preserving order
  const seen = new Set();
  const out = [];
  for (const w of words) {
    const lw = w.toLowerCase();
    if (lw.length < 2) continue;
    if (stop.has(lw)) continue;
    if (seen.has(lw)) continue;
    seen.add(lw);
    out.push(w);
    if (out.length >= (ANALYSIS_CONFIG?.maxKeywords ?? 150)) break;
  }
  return out;
}

/**
 * Count safe occurrences of a term in text using a guarded regex.
 * Example: "C++" in "C++ and C++14" → 2
 */
export function safeCount(term, text) {
  if (!term || !text) return 0;
  const re = makeWordMatcher(term);
  let n = 0;
  while (re.exec(text)) n += 1;
  return n;
}

/**
 * Build a keyword analysis summary:
 * - matched: terms present in resume
 * - missing: terms not present
 * - relevanceScore: 0–100 scaled by match ratio
 */
export function scoreKeywordMatch(jobText = '', resumeText = '') {
  const keywords = extractKeywordsFromText(jobText);
  const matched = [];
  const missing = [];

  for (const k of keywords) {
    const count = safeCount(k, resumeText);
    if (count > 0) matched.push({ term: k, count });
    else missing.push(k);
  }

  const total = keywords.length || 1;
  const score = Math.round((matched.length / total) * 100);

  return {
    keywords,
    matched,
    missing,
    relevanceScore: score
  };
}
